import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FetchHook from "../../../../Api/FetchHooks";
import PageLoader from "../../../../Components/PageLoader";
import {
  formatCurrencyV2,
  formatDate,
  getUserDetails,
} from "../../../../utils/helpers";
import { useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import PostHooks from "../../../../Api/PostHooks";
import WarningIcon from "@mui/icons-material/Warning";
import AlertComponent from "../../../../Components/AlertComponent";
import TableComponent from "../../../../Components/TableComponent";

const UserLoanInstalment = ({ user, loan }) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm();
  const navigate = useNavigate();
  const loggedInUser = getUserDetails();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [dialog, setDialog] = useState({
    open: false,
    type: null,
    label: null,
  });

  const openDialog = (type, label) => setDialog({ open: true, type, label });
  const closeDialog = () => setDialog({ open: false, type: null, label: null });

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const [instalment, setInstalment] = useState(false);

  const [loading, response, request] = FetchHook();
  const [reverseLoading, reverseResponse, reverseRequest] = PostHooks();

  const fetchData = useCallback(async () => {
    request({
      url: `instalment?loan_id='${loan.id}'`,
    });
  }, [loan]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const reverseInstalment = async (e) => {
    try {
      let formData = {
        entity_id: user?.id,
        loan_id: instalment?.actual_loan_id,
        amount: instalment?.actual_paid_amount,
        instalment_id: instalment?.id,
      };

      const revRequest = await reverseRequest({
        url: "/reverse_loan_instalment",
        body: formData,
      });

      if (revRequest && revRequest.data.StatusCode === 0) {
        setSnackbar({
          open: true,
          severity: "success",
          message: "Reverse successful!",
        });
        setTimeout(() => {
          fetchData();
        }, 1000);
      } else if (
        revRequest &&
        (revRequest.status === 200 || revRequest.status !== 200) &&
        revRequest.data.StatusCode !== 0
      ) {
        const errorMessage =
          revRequest?.data?.StatusMessage ??
          revRequest?.data?.message ??
          revRequest.message ??
          "An unexpected error occurred. Please try again.";

        setSnackbar({
          open: true,
          severity: "error",
          message: errorMessage,
        });
      }
    } catch (error) {
      console.log("reverse err", error);

      const errorMessage =
        error.response?.data?.StatusMessage ??
        error.response?.data?.message ??
        error.message ??
        "An unexpected error occurred. Please try again.";

      setSnackbar({
        open: true,
        severity: "error",
        message: errorMessage,
      });
    }
  };

  const columns = [
    {
      field: "id",
      headerName: "Id",
      width: 150,
    },
    {
      field: "actual_loan_id",
      headerName: "Actual Loan Id",
      width: 150,
    },
    {
      field: "instal_type",
      headerName: "Instalment type",
      width: 150,
    },
    {
      field: "expected_collection_amount",
      headerName: "Expected collection amount",
      width: 150,
      valueFormatter: (params) => formatCurrencyV2(params?.value),
    },
    {
      field: "actual_paid_amount",
      headerName: "Actual paid amount",
      width: 150,
      valueFormatter: (params) => formatCurrencyV2(params?.value),
    },
    {
      field: "balance_amount_payable",
      headerName: "Balance amount payable",
      width: 150,
      valueFormatter: (params) => formatCurrencyV2(params?.value),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 300,
    },
    {
      field: "date_expected",
      headerName: "Date expected",
      width: 200,
      valueFormatter: (params) => formatDate(params?.value),
    },
    // {
    //   field: "date_queued",
    //   headerName: "Date queued",
    //   width: 300,
    //   valueFormatter: (params) =>
    //     moment(params?.value).format("DD/MM/YYYY hh:mm A"),
    // },
    // {
    //   field: "date_created",
    //   headerName: "Date created",
    //   width: 300,
    //   valueFormatter: (params) =>
    //     moment(params?.value).format("DD/MM/YYYY hh:mm A"),
    // },
    {
      field: "date_last_payment",
      headerName: "Last payment date",
      width: 200,
      valueFormatter: (params) => formatDate(params?.value),
    },
    {
      field: "loan_instalment_details",
      headerName: "Actions",
      width: 200,
      renderCell: (row) => (
        <>
          {loggedInUser?.role === "admin" && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() =>
                  navigate(
                    "/dashboard/users/user-profile/loan-instalments/loan-instalment-details",
                    {
                      state: row.row,
                    },
                  )
                }
              >
                Details
              </Button>

              <Button
                variant="contained"
                onClick={() => {
                  setInstalment(row.row);
                  openDialog("reverse_instalment", "Reverse Instalment");
                }}
              >
                Reverse
              </Button>
            </Box>
          )}
        </>
      ),
    },
  ];

  return (
    <Box>
      <AlertComponent
        open={snackbar.open}
        handleSnackbar={handleSnackbarClose}
        severity={{ severity: snackbar.severity, message: snackbar.message }}
      />

      <Dialog open={dialog.open} onClose={closeDialog}>
        <DialogTitle id="form-dialog-title">Reverse Instalment</DialogTitle>
        <DialogContent
          sx={{
            my: "20px",
            width: { sm: "400px" },
          }}
        >
          <form onSubmit={handleSubmit(reverseInstalment)}>
            <Grid
              display={"flex"}
              direction={"column"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <WarningIcon color="warning" sx={{ fontSize: 100 }} />
              <Typography align="center">
                You are about to reverse an instalment of <br /> amount:{" "}
                {formatCurrencyV2(instalment?.actual_paid_amount)}
              </Typography>
            </Grid>

            <Box
              sx={{
                gap: 2,
                pt: "40px",
                display: "flex",
                direction: "row",
                justifyContent: "flex-end",
              }}
            >
              <Button variant="text" type="reset" onClick={() => closeDialog()}>
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                type="submit"
                loading={reverseLoading}
              >
                Confirm
              </LoadingButton>
            </Box>
          </form>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader
          sx={{
            px: "30px",
          }}
          title={
            <Typography variant="h6">
              Loan instalments(Loan_id: {loan?.id})
            </Typography>
          }
          action={
            <Box>
              {/* <FormControl
                sx={{
                  width: "20vw",
                }}
              >
                <InputLabel id="status" size="small">Status</InputLabel>

                <Select
                  labelId="loan_status"
                  id="loan_status"
                  value={status}
                  label="status"
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setCheck(0);
                  }}
                  size="small"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="domant">Dormant</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="incomplete">Incomplete</MenuItem>
                  <MenuItem value="complete">Complete</MenuItem>
                </Select>
              </FormControl> */}

              {/* <Button
                variant="contained"
                color="error"
                onClick={() => navigate(-1)}
              >
                Back
              </Button> */}
            </Box>
          }
        />
        <CardContent>
          {response && response.status === 200 ? (
            <TableComponent
              data={response.data.Instalment}
              columns={columns}
              loading={loading}
              sortModel={[
                {
                  field: "date_last_payment",
                  sort: "desc",
                },
              ]}
            />
          ) : (
            <PageLoader />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserLoanInstalment;
